import { useEffect, useState } from "react";
import { Smartphone, Share, X, Download } from "lucide-react";

const STORAGE_DISMISSED = "haccp_install_dismissed_v1";

function isInstalled() {
    if (typeof window === "undefined") return false;
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator.standalone === true) return true; // iOS
    return false;
}

function isIOS() {
    if (typeof window === "undefined") return false;
    return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

function isSafari() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent;
    return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export default function InstallAppButton({ variant = "button", className = "" }) {
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(isInstalled());
    const [showIosModal, setShowIosModal] = useState(false);

    useEffect(() => {
        const onBefore = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferred(null);
        };
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    async function handleInstall() {
        if (deferred) {
            deferred.prompt();
            try {
                await deferred.userChoice;
            } catch {}
            setDeferred(null);
            return;
        }
        if (isIOS()) {
            setShowIosModal(true);
            return;
        }
        // Fallback for browsers without beforeinstallprompt
        setShowIosModal(true);
    }

    if (installed) {
        return variant === "badge" ? (
            <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="install-status-ok">
                ✓ App installata sul dispositivo
            </span>
        ) : null;
    }

    // When not installed, the "badge" variant renders nothing (only the regular button does)
    if (variant === "badge") return null;

    const Button = (
        <button
            type="button"
            onClick={handleInstall}
            className={`btn-primary inline-flex items-center gap-2 text-sm ${className}`}
            data-testid="install-app-btn"
        >
            <Download size={16} /> Installa app sul telefono
        </button>
    );

    return (
        <>
            {Button}
            {showIosModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
                    onClick={() => setShowIosModal(false)}
                    data-testid="install-modal"
                >
                    <div
                        className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 py-4 border-b border-soft flex items-center justify-between">
                            <h3 className="font-display text-lg font-semibold text-forest-dark inline-flex items-center gap-2">
                                <Smartphone size={20} className="text-forest" />
                                Installa l'app
                            </h3>
                            <button onClick={() => setShowIosModal(false)} className="p-2 hover:bg-bone-2 rounded-lg" data-testid="install-modal-close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <p className="text-sm text-stone-600">
                                Per ricevere le notifiche push anche con l'app chiusa, aggiungi Samir HACCP alla schermata Home del telefono.
                            </p>

                            {isIOS() && isSafari() ? (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-forest-500 mb-3">
                                        Su iPhone / iPad (Safari)
                                    </p>
                                    <ol className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <span className="w-7 h-7 rounded-full bg-forest text-white grid place-items-center font-semibold text-sm shrink-0">
                                                1
                                            </span>
                                            <p className="text-sm text-stone-700 pt-0.5">
                                                Tocca l'icona di condivisione <Share size={14} className="inline" /> nella barra in basso di Safari.
                                            </p>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="w-7 h-7 rounded-full bg-forest text-white grid place-items-center font-semibold text-sm shrink-0">
                                                2
                                            </span>
                                            <p className="text-sm text-stone-700 pt-0.5">
                                                Scorri e tocca <strong>"Aggiungi alla schermata Home"</strong>.
                                            </p>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="w-7 h-7 rounded-full bg-forest text-white grid place-items-center font-semibold text-sm shrink-0">
                                                3
                                            </span>
                                            <p className="text-sm text-stone-700 pt-0.5">
                                                Conferma toccando <strong>"Aggiungi"</strong>. L'icona Samir HACCP comparirà sulla Home.
                                            </p>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="w-7 h-7 rounded-full bg-forest text-white grid place-items-center font-semibold text-sm shrink-0">
                                                4
                                            </span>
                                            <p className="text-sm text-stone-700 pt-0.5">
                                                Apri l'app dall'icona Home e autorizza le notifiche da Impostazioni.
                                            </p>
                                        </li>
                                    </ol>
                                    <p className="text-xs text-stone-500 mt-4 italic">
                                        Richiede iOS 16.4 o superiore per ricevere le notifiche push.
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-forest-500 mb-3">
                                        Su Android (Chrome / Edge / Brave)
                                    </p>
                                    <ol className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <span className="w-7 h-7 rounded-full bg-forest text-white grid place-items-center font-semibold text-sm shrink-0">
                                                1
                                            </span>
                                            <p className="text-sm text-stone-700 pt-0.5">
                                                Apri il menu del browser (i tre puntini in alto a destra).
                                            </p>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="w-7 h-7 rounded-full bg-forest text-white grid place-items-center font-semibold text-sm shrink-0">
                                                2
                                            </span>
                                            <p className="text-sm text-stone-700 pt-0.5">
                                                Tocca <strong>"Installa app"</strong> o <strong>"Aggiungi a schermata Home"</strong>.
                                            </p>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="w-7 h-7 rounded-full bg-forest text-white grid place-items-center font-semibold text-sm shrink-0">
                                                3
                                            </span>
                                            <p className="text-sm text-stone-700 pt-0.5">
                                                Conferma. L'app comparirà come icona nella schermata Home.
                                            </p>
                                        </li>
                                    </ol>
                                    <p className="text-xs text-stone-500 mt-4 italic">
                                        Se non vedi l'opzione, prova a ricaricare la pagina o usa Chrome.
                                    </p>
                                </div>
                            )}

                            <button onClick={() => setShowIosModal(false)} className="btn-ghost text-sm w-full" data-testid="install-modal-dismiss">
                                Ho capito
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
